import { type Command, type Editor, Extension, Mark, mergeAttributes, Node } from '@tiptap/core';
import { Collaboration } from '@tiptap/extension-collaboration';
import { Highlight } from '@tiptap/extension-highlight';
import { TaskItem, TaskList } from '@tiptap/extension-list';
import { Placeholder } from '@tiptap/extension-placeholder';
import { TableKit } from '@tiptap/extension-table';
import { TextAlign } from '@tiptap/extension-text-align';
import { Color, FontFamily, FontSize, TextStyle } from '@tiptap/extension-text-style';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import type { Doc } from 'yjs';

import { FindReplace } from './find-replace';
import { DocumentFileView, DocumentImageView } from './NodeViews';

/**
 * The document's vocabulary. Keep in step with the backend's converters
 * (`documents/content/schema.py`): a node added here and not there is lost from exports.
 */
export const ALLOWED_LINK_PROTOCOLS = ['http', 'https', 'mailto', 'tel'];

/** Only harmless absolute addresses become links, whatever a paste or a peer brings. */
export function isSafeLink(url: string): boolean {
  try {
    return ALLOWED_LINK_PROTOCOLS.includes(new URL(url, 'https://x.invalid').protocol.slice(0, -1));
  } catch {
    return false;
  }
}

/** The fonts and sizes the toolbar offers; the backend keeps the same list (`content/schema.py`). */
export const FONT_FAMILIES = [
  'Arial',
  'Georgia',
  'Times New Roman',
  'Verdana',
  'Courier New',
] as const;
/** What a new document is written in until the author picks something else. */
export const DEFAULT_FONT_FAMILY = 'Arial';
export const DEFAULT_FONT_SIZE = 14;
/** The range of a font size in points: the same one the exports accept (`content/schema.py`). */
export const MIN_FONT_SIZE = 6;
export const MAX_FONT_SIZE = 144;

/**
 * A size typed by a person -> points, or `null` when it is not a usable size. Accepts "14", "17.5",
 * "17,5" and "14pt"; one decimal at most (half-point steps are what a document can show);
 * nothing else (letters, signs, exponents, empty, zero, out of range).
 */
export function parseFontSize(raw: string): number | null {
  const match = /^(\d{1,3})(?:[.,](\d))?\s*(?:pt)?$/i.exec(raw.trim());
  if (!match) return null;
  const value = Number(`${match[1] ?? ''}${match[2] ? `.${match[2]}` : ''}`);
  return value >= MIN_FONT_SIZE && value <= MAX_FONT_SIZE ? value : null;
}

/** `14` -> "14", `17.5` -> "17.5". */
export const formatFontSize = (pt: number): string => String(Math.round(pt * 10) / 10);

/**
 * The size the toolbar shows for the selection: the one size shared by all its text, the default
 * where nothing is styled, or `'mixed'` when the selected text has different sizes. A collapsed
 * cursor reports what newly typed text gets (stored marks included).
 */
export function selectionFontSize(editor: Editor): string {
  const { from, to, empty } = editor.state.selection;
  const sizeOf = (raw: unknown): string => {
    const pt = typeof raw === 'string' ? Number.parseFloat(raw) : Number.NaN;
    return Number.isFinite(pt) ? formatFontSize(pt) : String(DEFAULT_FONT_SIZE);
  };
  if (empty) {
    return sizeOf((editor.getAttributes('textStyle') as { fontSize?: string }).fontSize);
  }
  const sizes = new Set<string>();
  editor.state.doc.nodesBetween(from, to, (node) => {
    if (!node.isText) return;
    const mark = node.marks.find((m) => m.type.name === 'textStyle');
    sizes.add(sizeOf((mark?.attrs as { fontSize?: string } | undefined)?.fontSize));
  });
  if (sizes.size > 1) return 'mixed';
  return sizes.values().next().value ?? String(DEFAULT_FONT_SIZE);
}

/** The swatches of the colour and highlight tools (toolbar and selection menu). */
export const COLORS = ['#c0392b', '#d35400', '#1e8449', '#1f618d', '#6c3483', '#616a6b'];
export const HIGHLIGHTS = ['#fff59d', '#c8e6c9', '#bbdefb', '#f8bbd0', '#e1bee7'];
export const FONT_SIZES = [10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36] as const;
export const LINE_HEIGHTS = ['1', '1.15', '1.5', '2'] as const;
export const SPACE_AFTER = ['0', '6', '12', '24'] as const;
export const MAX_INDENT = 8;

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    paragraphFormat: {
      setLineHeight: (value: string | null) => ReturnType;
      setSpaceAfter: (value: string | null) => ReturnType;
      indentBlock: () => ReturnType;
      outdentBlock: () => ReturnType;
    };
  }
}

const BLOCKS = ['paragraph', 'heading'] as const;

interface BlockLayout {
  lineHeight?: string | null;
  spaceAfter?: string | null;
  indent?: number | null;
}

const asText = (value: unknown): string =>
  typeof value === 'string' || typeof value === 'number' ? String(value) : '';

const indentOf = (editor: Editor): number =>
  (editor.getAttributes('paragraph') as BlockLayout).indent ??
  (editor.getAttributes('heading') as BlockLayout).indent ??
  0;

/**
 * Paragraph-level layout on paragraphs and headings: line spacing, the space after and the
 * indentation. They are node attributes (so they sync and export with the block), not styles
 * pasted on text; `null` means "the default".
 */
export const ParagraphFormat = Extension.create({
  name: 'paragraphFormat',
  addGlobalAttributes() {
    return [
      {
        types: [...BLOCKS],
        attributes: {
          lineHeight: {
            default: null,
            parseHTML: (element: HTMLElement) => element.style.lineHeight || null,
            renderHTML: (attributes: BlockLayout) =>
              attributes.lineHeight
                ? { style: `line-height: ${asText(attributes.lineHeight)}` }
                : {},
          },
          spaceAfter: {
            default: null,
            parseHTML: (element: HTMLElement) => element.dataset.spaceAfter ?? null,
            renderHTML: (attributes: BlockLayout) =>
              attributes.spaceAfter === null || attributes.spaceAfter === undefined
                ? {}
                : {
                    'data-space-after': asText(attributes.spaceAfter),
                    style: `margin-bottom: ${asText(attributes.spaceAfter)}pt`,
                  },
          },
          indent: {
            default: null,
            parseHTML: (element: HTMLElement) => Number(element.dataset.indent) || null,
            renderHTML: (attributes: BlockLayout) =>
              typeof attributes.indent === 'number' && attributes.indent > 0
                ? {
                    'data-indent': String(attributes.indent),
                    style: `margin-left: ${String(attributes.indent * 2)}rem`,
                  }
                : {},
          },
        },
      },
    ];
  },
  addCommands() {
    const apply =
      (attrs: BlockLayout): Command =>
      ({ chain }) =>
        BLOCKS.reduce((commands, type) => commands.updateAttributes(type, attrs), chain()).run();
    return {
      setLineHeight: (value) => apply({ lineHeight: value }),
      setSpaceAfter: (value) => apply({ spaceAfter: value }),
      indentBlock: () => (props) =>
        apply({ indent: Math.min(indentOf(props.editor) + 1, MAX_INDENT) })(props),
      outdentBlock: () => (props) => {
        const current = indentOf(props.editor);
        return apply({ indent: current > 1 ? current - 1 : null })(props);
      },
    };
  },
  addKeyboardShortcuts() {
    return {
      'Mod-]': () => this.editor.commands.indentBlock(),
      'Mod-[': () => this.editor.commands.outdentBlock(),
    };
  },
});

/** An uploaded image: it refers to a media id, never to a URL or to embedded bytes. */
export const DocumentImage = Node.create({
  name: 'documentImage',
  group: 'block',
  atom: true,
  draggable: true,
  addOptions: () => ({ documentId: '', mediaBase: '' }),
  addAttributes: () => ({
    mediaId: { default: null },
    alt: { default: null },
    width: { default: null },
    align: { default: null },
  }),
  parseHTML: () => [{ tag: 'figure[data-document-image]' }],
  renderHTML: ({ HTMLAttributes }) => [
    'figure',
    mergeAttributes(
      { 'data-document-image': '' },
      { 'data-media': String(HTMLAttributes.mediaId) },
    ),
  ],
  addNodeView() {
    return ReactNodeViewRenderer(DocumentImageView);
  },
});

/** An attached file: a recognizable block that opens or downloads through the document's rights. */
export const DocumentFile = Node.create({
  name: 'documentFile',
  group: 'block',
  atom: true,
  draggable: true,
  addOptions: () => ({ documentId: '', mediaBase: '' }),
  addAttributes: () => ({
    mediaId: { default: null },
    filename: { default: null },
    size: { default: null },
    mime: { default: null },
  }),
  parseHTML: () => [{ tag: 'div[data-document-file]' }],
  renderHTML: ({ HTMLAttributes }) => [
    'div',
    mergeAttributes({ 'data-document-file': '' }, { 'data-media': String(HTMLAttributes.mediaId) }),
  ],
  addNodeView() {
    return ReactNodeViewRenderer(DocumentFileView);
  },
});

/** Superscript / subscript: marks of their own in the shared document (the backend whitelists the
 * same two names), mutually exclusive as in every word processor. */
export const Superscript = Mark.create({
  name: 'superscript',
  excludes: 'subscript',
  parseHTML: () => [{ tag: 'sup' }],
  renderHTML: ({ HTMLAttributes }) => ['sup', HTMLAttributes, 0],
  addKeyboardShortcuts: () => ({
    'Mod-.': ({ editor }) => editor.commands.toggleMark('superscript'),
  }),
});

export const Subscript = Mark.create({
  name: 'subscript',
  excludes: 'superscript',
  parseHTML: () => [{ tag: 'sub' }],
  renderHTML: ({ HTMLAttributes }) => ['sub', HTMLAttributes, 0],
  addKeyboardShortcuts: () => ({
    'Mod-,': ({ editor }) => editor.commands.toggleMark('subscript'),
  }),
});

/**
 * `doc` is the shared collaboration document of a note. Without it the editor is a plain,
 * stand-alone one (its own undo history, nothing synchronised): that is how a template's content
 * is edited, with its files served from `mediaBase`.
 */
export function buildExtensions({
  doc,
  documentId,
  placeholder,
  mediaBase = '',
}: {
  doc?: Doc | undefined;
  documentId: string;
  placeholder: string;
  mediaBase?: string;
}) {
  return [
    StarterKit.configure({
      undoRedo: doc ? false : {}, // Collaboration brings an undo that only undoes this person's own edits
      heading: { levels: [1, 2, 3, 4, 5, 6] },
      link: {
        openOnClick: false,
        autolink: true,
        protocols: ALLOWED_LINK_PROTOCOLS,
        isAllowedUri: (url) => isSafeLink(url),
        HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' },
      },
    }),
    TextStyle,
    Color,
    FontFamily,
    FontSize,
    ParagraphFormat,
    Superscript,
    Subscript,
    FindReplace,
    Highlight.configure({ multicolor: true }),
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    TaskList,
    TaskItem.configure({ nested: true }),
    TableKit.configure({ table: { resizable: true } }),
    DocumentImage.configure({ documentId, mediaBase }),
    DocumentFile.configure({ documentId, mediaBase }),
    Placeholder.configure({ placeholder }),
    ...(doc ? [Collaboration.configure({ document: doc, field: 'default' })] : []),
  ];
}
